// pipe receiving→eval wave (slice B): the printed text of an inbound PALLET label — PURE (no
// React, no DOM, no i18n runtime: the caller passes the localized words), so the intake screen and
// the job page's reprint build the SAME label from one place. The barcode stays the pallet's
// `label_code` (slice A mints the compact `INB<job>P<nn>`, printed under the barcode as
// `INB-<job>-P<nn>` via `displayCode`); "Pallet n of N" is printed text, never part of the barcode.
// Printed through the ONE label queue (`printLabels` / ZPL) — no second printer path.
import type { useI18n } from '@affine/i18n';

import { displayCode } from './display-code';
import type { LabelItem } from './zpl';

/** The localized words of the label, supplied by the caller's `useI18n()`. */
export interface PalletLabelWords {
  /** "Job 1042" */
  job: (jobNo: string) => string;
  /** "Pallet 2" — the job's expected pallet count is unknown. */
  pallet: (n: number) => string;
  /** "Pallet 2 of 4" */
  palletOf: (n: number, total: number) => string;
  /** "500 lb" — `weight` is already number-formatted. */
  gross: (weight: string) => string;
}

export interface PalletLabelInput {
  /** The scannable code (the container's `label_code`). */
  code: string;
  /** The job number as printed (`order_no`, else the job title); null ⇒ no job line. */
  jobNo: string | null;
  /** The customer's display name; null when unknown OR redacted — never print a masked value. */
  customer: string | null;
  /** Weighed gross in pounds; null ⇒ the weight is left off. */
  grossLb: number | null;
  /** The receiving date, already formatted for the reader's locale; null ⇒ left off. */
  receivedOn: string | null;
  /** The per-job pallet ordinal (`container.pallet_no`, slice A); null ⇒ no pallet line. */
  palletNo: number | null;
  /** The job's expected pallet count (`inbound_order.expected_pallet_count`); null ⇒ "Pallet n". */
  expectedCount: number | null;
}

/** A positive whole number, else null — a count/ordinal that is not one prints nothing. */
const whole = (n: number | null): number | null =>
  n !== null && Number.isInteger(n) && n > 0 ? n : null;

// Module-level (the standing Intl rule): pounds to at most one decimal, grouped.
const LB_FORMAT = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});

/** PURE: "Pallet n of N" / "Pallet n" / null (n unknown). An expected count below n is still
 *  printed as given — the count is advisory, never a limit (a fifth pallet on a job of four is
 *  received normally and reads "Pallet 5 of 4" until the office corrects the count). */
export function palletOrdinalText(
  palletNo: number | null,
  expectedCount: number | null,
  words: Pick<PalletLabelWords, 'pallet' | 'palletOf'>
): string | null {
  const n = whole(palletNo);
  if (n === null) return null;
  const total = whole(expectedCount);
  return total === null ? words.pallet(n) : words.palletOf(n, total);
}

/** PURE: the queued label for one inbound pallet — the stored code as the barcode, its printed
 *  form under it, and three head lines above the barcode:
 *  `Job 1042 · Customer`, `Pallet n of N`, `500 lb · <date>`; any unknown part is dropped, never
 *  printed as a placeholder. */
export function palletLabelItem(
  input: PalletLabelInput,
  words: PalletLabelWords
): LabelItem {
  const jobLine = [input.jobNo ? words.job(input.jobNo) : null, input.customer]
    .filter(Boolean)
    .join(' · ');
  const weighed =
    input.grossLb !== null &&
    Number.isFinite(input.grossLb) &&
    input.grossLb > 0
      ? words.gross(LB_FORMAT.format(input.grossLb))
      : null;
  const factLine = [weighed, input.receivedOn].filter(Boolean).join(' · ');
  const lines = [
    jobLine,
    palletOrdinalText(input.palletNo, input.expectedCount, words),
    factLine,
  ].filter((l): l is string => !!l);
  const code = { code: input.code, codeText: displayCode(input.code) };
  return lines.length > 0 ? { ...code, lines } : code;
}

/** The label words from the active translator (type-only i18n import — the module stays pure). */
export function palletLabelWords(
  t: ReturnType<typeof useI18n>
): PalletLabelWords {
  return {
    job: jobNo => t['com.affine.pipe.labels.pallet.job']({ job: jobNo }),
    pallet: n => t['com.affine.pipe.labels.pallet.n']({ n: String(n) }),
    palletOf: (n, total) =>
      t['com.affine.pipe.labels.pallet.of']({
        n: String(n),
        total: String(total),
      }),
    gross: weight => t['com.affine.pipe.labels.pallet.gross']({ weight }),
  };
}
